import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles, Gem, Truck, Check } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import StoriesRail from "@/components/StoriesRail";
import Reveal from "@/components/Reveal";
import SeoHead from "@/components/SeoHead";
import { useBranding } from "@/context/BrandingContext";

const LIFESTYLE_IMG = "https://images.pexels.com/photos/9489731/pexels-photo-9489731.jpeg?auto=compress&cs=tinysrgb&w=1200";

const Home = () => {
  const { branding } = useBranding();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [homeSeo, setHomeSeo] = useState(null);

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    api.get("/products", { params: { tag: "featured", limit: 4 } }).then(r => setFeatured(r.data)).catch(() => {});
    api.get("/products", { params: { tag: "best_seller", limit: 8 } }).then(r => setBestSellers(r.data)).catch(() => {});
    api.get("/seo/home").then(r => setHomeSeo(r.data)).catch(() => {});
  }, []);

  const filteredBS = activeTab === "all" ? bestSellers : bestSellers.filter(p => p.category_slug === activeTab);
  const brandTitle = `${branding?.brand_name ? branding.brand_name.charAt(0).toUpperCase() + branding.brand_name.slice(1) : "Rieave"} — ${branding?.tagline || "Adorn Your Story"}`;

  return (
    <div className="font-body text-charcoal" data-testid="home-page">
      <SeoHead
        title={homeSeo?.title || brandTitle}
        description={homeSeo?.description || "Timeless jewelry, crafted for every day. Shop necklaces, earrings, rings and bracelets."}
        keywords={homeSeo?.keywords || "rieave, jewelry, necklaces, earrings, rings, bracelets, gold plated, handcrafted, India"}
        image={homeSeo?.og_image || branding?.logo_url || ""}
        canonical={typeof window !== "undefined" ? window.location.origin + "/" : ""}
      />
      {/* HERO — full-bleed, centered, with purple gradient overlay — matches rieave.lovable.app */}
      <section className="relative min-h-[92vh] md:min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.08, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src="https://rieave.lovable.app/assets/hero-bg-BKw7tFPD.jpg"
            alt="Elegant jewelry collection"
            className="w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(135deg, rgba(126, 80, 149, 0.65), rgba(186, 94, 140, 0.55), rgba(142, 83, 172, 0.6))" }}
          />
        </motion.div>
        <div className="relative container mx-auto px-6 pt-32 pb-20 text-center">
          <motion.div
            className="max-w-3xl mx-auto"
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
          >
            <motion.span
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
              className="inline-block font-label text-[13px] tracking-[0.3em] uppercase text-white/85 mb-6"
              data-testid="hero-eyebrow"
            >
              Jewelry That Celebrates You
            </motion.span>
            <motion.h1
              variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } } }}
              className="font-hero text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.08] mb-6 text-white"
            >
              Timeless Elegance,<br />
              <span className="serif-italic">Uniquely Yours</span>
            </motion.h1>
            <motion.p
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.7 } } }}
              className="font-body text-lg md:text-xl text-white/90 max-w-xl mx-auto mb-10 leading-relaxed"
            >
              Each piece is thoughtfully designed to blend timeless elegance with everyday versatility. With {branding.brand_name || "Rieave"}, you don't follow trends — you create them.
            </motion.p>
            <motion.div
              variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.7 } } }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link
                to="/shop"
                className="group inline-flex items-center justify-center gap-2 h-14 rounded-xl px-10 text-base md:text-lg font-body font-medium bg-white/95 hover:bg-white text-charcoal shadow-lg shadow-black/10 transition-all duration-300"
                data-testid="hero-cta-primary"
              >
                Explore Collection
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* STORIES RAIL — Instagram-style circular shortcuts (managed in Admin → Stories) */}
      <StoriesRail />

      {/* CATEGORY CARDS */}
      <section className="py-14" data-testid="categories-section">
        <div className="rieave-container">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
              <div>
                <p className="eyebrow mb-2">Curated for you</p>
                <h2 className="font-hero text-4xl md:text-5xl text-charcoal">Shop by <span className="serif-italic text-rose">edit</span></h2>
              </div>
              <Link to="/shop" className="btn-link">All jewelry →</Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.1}>
                <Link to={`/shop/${c.slug}`} className="group relative aspect-[3/4] rounded-[24px] overflow-hidden bg-blush block" data-testid={`category-card-${c.slug}`}>
                  <img src={c.image_url} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="eyebrow text-cream/80 mb-1">Shop</p>
                    <h3 className="font-hero text-2xl text-cream">{c.name}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-14 bg-lavender/40" data-testid="featured-section">
        <div className="rieave-container">
          <Reveal>
            <div className="flex items-end justify-between mb-8 gap-4">
              <div>
                <p className="eyebrow mb-2">Featured</p>
                <h2 className="font-hero text-4xl md:text-5xl">Loved the <span className="serif-italic text-rose">most.</span></h2>
              </div>
              <Link to="/shop" className="btn-link hidden md:inline-flex">View all →</Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08} y={18}>
                <ProductCard product={p} badge={p.tags?.includes("new") ? "New" : null} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20" data-testid="why-section">
        <div className="rieave-container grid grid-cols-1 md:grid-cols-4 gap-8">
          {[
            { icon: Gem, title: "Ethically Sourced", body: "Responsibly crafted, transparent from material to finish." },
            { icon: ShieldCheck, title: "Certified Quality", body: "Every piece is tested for plating integrity and finish." },
            { icon: Sparkles, title: "Lifetime Care", body: "Re-polishing and gentle repairs — on us, always." },
            { icon: Truck, title: "Secure Shipping", body: `Free above ₹${branding.free_shipping_threshold || 1499}, insured and tracked.` },
          ].map((it, idx) => (
            <Reveal key={idx} delay={idx * 0.1}>
              <div className="group">
                <div className="w-12 h-12 rounded-full bg-blush flex items-center justify-center mb-5 text-rose group-hover:bg-rose group-hover:text-cream transition">
                  <it.icon size={20} />
                </div>
                <h3 className="font-cormorant text-2xl mb-2">{it.title}</h3>
                <p className="text-mauve text-sm leading-relaxed">{it.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BEST SELLERS WITH TABS */}
      <section className="py-14 bg-blush/40" data-testid="bestsellers-section">
        <div className="rieave-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <p className="eyebrow mb-2">Most loved</p>
              <h2 className="font-hero text-4xl md:text-5xl">Best <span className="serif-italic text-rose">sellers</span></h2>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[{ k: "all", l: "All" }, { k: "necklaces", l: "Necklaces" }, { k: "earrings", l: "Earrings" }, { k: "rings", l: "Rings" }, { k: "bracelets", l: "Bracelets" }].map((t) => (
                <button
                  key={t.k} onClick={() => setActiveTab(t.k)}
                  className={`px-5 py-2 rounded-full font-label text-[11px] uppercase tracking-[0.22em] transition ${
                    activeTab === t.k ? "bg-charcoal text-cream" : "bg-white border border-border text-charcoal hover:bg-lavender/60"
                  }`}
                  data-testid={`bestsellers-tab-${t.k}`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filteredBS.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} badge="Best seller" />)}
          </div>
        </div>
      </section>

      {/* PROMO BANNERS */}
      <section className="py-14">
        <div className="rieave-container grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { eyebrow: "New arrival", title: "The Bloom\nCollection", body: "Inspired by spring gardens.", cta: "Explore", to: "/shop/rings", bg: "bg-blush" },
            { eyebrow: "Limited edition", title: "Rose Gold\nEssentials", body: "Warm-toned everyday luxe.", cta: "Shop now", to: "/shop/necklaces", bg: "bg-lavender" },
            { eyebrow: "Bestsellers", title: "The Everyday\nEdit", body: "Pieces you'll never take off.", cta: "Discover", to: "/shop", bg: "bg-gold/40" },
          ].map((b, i) => (
            <Link key={i} to={b.to} className={`${b.bg} rounded-[24px] p-8 aspect-[5/6] flex flex-col justify-between group transition-transform hover:-translate-y-1`} data-testid={`promo-banner-${i}`}>
              <div>
                <p className="eyebrow mb-3">{b.eyebrow}</p>
                <h3 className="font-hero text-4xl leading-[1.05] whitespace-pre-line">{b.title}</h3>
              </div>
              <div>
                <p className="mb-4 text-mauve">{b.body}</p>
                <span className="btn-link">{b.cta} →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="py-14 bg-charcoal text-cream" data-testid="stats-section">
        <div className="rieave-container grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: "20+", l: "Years of craft" },
            { n: "50K+", l: "Happy customers" },
            { n: "100%", l: "Authentic guarantee" },
            { n: "4.9★", l: "Customer rating" },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div>
                <p className="font-hero text-5xl md:text-6xl text-gold">{s.n}</p>
                <p className="font-label text-xs uppercase tracking-[0.24em] text-cream/60 mt-2">{s.l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-20" data-testid="about-section">
        <div className="rieave-container grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="rounded-[24px] overflow-hidden aspect-[4/5]">
            <img src={LIFESTYLE_IMG} alt="rieave craft" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="eyebrow mb-3">About {branding.brand_name || "rieave"}</p>
            <h2 className="font-hero text-4xl md:text-5xl leading-[1.05] mb-5">
              Crafting jewelry that <span className="serif-italic text-rose">celebrates you.</span>
            </h2>
            <p className="text-charcoal/80 leading-relaxed mb-5">{branding.about_long}</p>
            <div className="flex flex-wrap gap-3 mb-6">
              {["Handcrafted", "Ethically sourced", "Timeless design"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 border border-border rounded-full px-4 py-1.5 font-label text-[11px] uppercase tracking-[0.2em] text-charcoal">
                  <Check size={12} className="text-rose" /> {t}
                </span>
              ))}
            </div>
            <Link to="/blog" className="btn-outline">Discover our story</Link>
          </div>
        </div>
      </section>

      {/* PRESS MARQUEE */}
      <section className="py-10 border-y border-border bg-cream overflow-hidden" data-testid="press-section">
        <p className="eyebrow text-center mb-5">As featured in</p>
        <div className="marquee-track">
          {["US Weekly", "Latestly", "Mid-Day", "Hindustan Times", "Zee News", "Vogue India", "Elle", "Grazia", "US Weekly", "Latestly", "Mid-Day", "Hindustan Times"].map((p, i) => (
            <span key={i} className="font-hero text-2xl md:text-3xl text-charcoal/60">{p}</span>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
